"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, AlertCircle } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  diagnosticErrorName,
  recordDeviceTestEvent,
  type DiagnosticPhase,
} from "./diagnostics";

interface SpeakerCheckProps {
  onPass: () => void;
}

function generateTestTone(audioCtx: AudioContext): AudioBufferSourceNode {
  const duration = 2;
  const sampleRate = audioCtx.sampleRate;
  const buffer = audioCtx.createBuffer(1, sampleRate * duration, sampleRate);
  const channel = buffer.getChannelData(0);

  // A gentle chime: two sine waves with fade in/out
  for (let i = 0; i < buffer.length; i++) {
    const t = i / sampleRate;
    const envelope = Math.min(t / 0.1, 1) * Math.min((duration - t) / 0.3, 1);
    channel[i] =
      envelope *
      0.3 *
      (Math.sin(2 * Math.PI * 523.25 * t) +
        0.5 * Math.sin(2 * Math.PI * 659.25 * t));
  }

  const source = audioCtx.createBufferSource();
  source.buffer = buffer;
  return source;
}

export function SpeakerCheck({ onPass }: SpeakerCheckProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>("");
  const [supportsSinkId, setSupportsSinkId] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const attemptRef = useRef(0);

  useEffect(
    () => () => {
      attemptRef.current += 1;
      if (audioCtxRef.current) {
        void audioCtxRef.current.close().catch(() => undefined);
        audioCtxRef.current = null;
      }
    },
    [],
  );

  const loadSpeakers = useCallback(async (attempt: number) => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (attempt !== attemptRef.current) return;
      const outputDevices = devices.filter((d) => d.kind === "audiooutput");
      setSpeakers(outputDevices);

      const audio = document.createElement("audio");
      const hasSinkId = "setSinkId" in audio;
      setSupportsSinkId(hasSinkId);

      if (outputDevices.length > 0 && outputDevices[0]) {
        setSelectedSpeaker(
          (selected) => selected || outputDevices[0]!.deviceId,
        );
      }
    } catch (err) {
      // enumerateDevices not available, skip speaker selection
      if (attempt === attemptRef.current) {
        recordDeviceTestEvent("failed", {
          step: "speaker",
          attempt,
          phase: "enumerate_devices",
          error_name: diagnosticErrorName(err),
          recoverable: true,
        });
      }
    }
  }, []);

  const playTestSound = useCallback(async () => {
    const attempt = ++attemptRef.current;
    const isCurrent = () => attempt === attemptRef.current;
    recordDeviceTestEvent("started", {
      step: "speaker",
      attempt,
      reason: attempt === 1 ? "initial" : "retry",
    });
    let phase: DiagnosticPhase = "audio_context";
    setIsPlaying(true);
    setError(null);

    try {
      await loadSpeakers(attempt);
      if (!isCurrent()) return;

      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;

      // Route to selected speaker if supported
      if (supportsSinkId && selectedSpeaker && "setSinkId" in audioCtx) {
        try {
          await (
            audioCtx as AudioContext & {
              setSinkId: (id: string) => Promise<void>;
            }
          ).setSinkId(selectedSpeaker);
        } catch (err) {
          // Fallback to default output
          if (isCurrent()) {
            recordDeviceTestEvent("failed", {
              step: "speaker",
              attempt,
              phase: "speaker_routing",
              error_name: diagnosticErrorName(err),
              recoverable: true,
            });
          }
        }
      }
      if (!isCurrent()) return;

      phase = "speaker_playback";
      const source = generateTestTone(audioCtx);
      source.connect(audioCtx.destination);
      source.start();

      source.onended = () => {
        if (!isCurrent()) return;
        recordDeviceTestEvent("ready", {
          step: "speaker",
          attempt,
          audio_context_state: audioCtx.state,
        });
        setIsPlaying(false);
        setHasPlayed(true);
        audioCtxRef.current = null;
        void audioCtx.close().catch(() => undefined);
      };
    } catch (err) {
      if (!isCurrent()) return;
      recordDeviceTestEvent("failed", {
        step: "speaker",
        attempt,
        phase,
        error_name: diagnosticErrorName(err),
        audio_context_state: audioCtxRef.current?.state,
      });
      if (audioCtxRef.current) {
        void audioCtxRef.current.close().catch(() => undefined);
        audioCtxRef.current = null;
      }
      setIsPlaying(false);
      setError("Greška pri reprodukciji zvuka.");
    }
  }, [loadSpeakers, supportsSinkId, selectedSpeaker]);

  const handleSpeakerChange = (deviceId: string) => {
    recordDeviceTestEvent("device_changed", { step: "speaker" });
    setSelectedSpeaker(deviceId);
  };

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="bg-muted/50 flex flex-col items-center gap-3 rounded-xl py-6">
        <div className="bg-primary/10 flex h-16 w-16 items-center justify-center rounded-full">
          <Volume2
            className={`text-primary h-8 w-8 ${isPlaying ? "animate-pulse" : ""}`}
          />
        </div>
        <Button
          onClick={() => void playTestSound()}
          disabled={isPlaying}
          variant={hasPlayed ? "outline" : "default"}
        >
          {isPlaying
            ? "Reprodukuje se..."
            : hasPlayed
              ? "Pusti ponovo"
              : "Pusti test zvuk"}
        </Button>
      </div>

      {supportsSinkId && speakers.length > 1 && (
        <div className="ph-no-capture">
          <Select value={selectedSpeaker} onValueChange={handleSpeakerChange}>
            <SelectTrigger className="ph-no-capture h-9 text-xs">
              <Volume2 className="mr-1.5 h-3.5 w-3.5 shrink-0" />
              <SelectValue placeholder="Zvučnik" />
            </SelectTrigger>
            <SelectContent className="ph-no-capture">
              {speakers.map((s) => (
                <SelectItem key={s.deviceId} value={s.deviceId}>
                  {s.label || "Zvučnik"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {hasPlayed && !error && (
        <div className="space-y-2">
          <Button
            onClick={() => {
              recordDeviceTestEvent("step_completed", {
                step: "speaker",
                attempt: attemptRef.current,
                method: "manual",
              });
              onPass();
            }}
            className="w-full"
          >
            Čujem zvuk
          </Button>
          <details className="text-muted-foreground text-xs">
            <summary className="cursor-pointer">Ne čujem zvuk?</summary>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>Proverite da li je jačina zvuka pojačana</li>
              <li>
                Proverite da li su slušalice ili Bluetooth uređaj povezani
              </li>
              <li>Pokušajte sa drugim izlaznim uređajem</li>
              <li>Proverite sistemska podešavanja zvuka</li>
            </ul>
          </details>
        </div>
      )}
    </div>
  );
}
